import type { ActionCardAction } from "components/shared/ActionCard";
import type { DeclarationStatus, TimeStatus } from "components/URSSAFDeclarationPage";
import type { IBeneficiar, IRepresentative } from "containers/Beneficiars/types";
import { calculateNIRControlKey } from "helpers/utils";
import { t } from "i18next";
import type { CSSProperties, ReactNode } from "react";
import type { IDeclaration } from "store/contributionStore";
import type { IPayment, IUrssafErrors } from "store/paymentSlice";
import type { TPeriod } from "store/periodStore";
import type { ICompany } from "store/settingsStore";

export type DeclarationsCardsData = {
    /**
     * Main title of the card
     */
    title: string;
    /**
     * Little description to show under the title
     */
    description: string | JSX.Element | undefined;
    /**
     * Optionnal icon to show on the left of the title
     */
    icon?: ReactNode;
    /**
     * Optionnal action
     * ```ts
     * {
     *    icon?: ReactNode
     *    label: string;
     *    onClick: () => void;
     * }
     * ```
     */
    action?: ActionCardAction | ActionCardAction[];
    /**
     * Optionnal style, using React.CSSProperties type
     */
    style?: CSSProperties;
    timeStatus: TimeStatus;
    sortingDate: Date;
    declarationStatus: DeclarationStatus;
    /**
     * O = year
     * 1-4 = quarter
     */
    quarter: 0 | 1 | 2 | 3 | 4;
    year: number;
    endDate: Date | string;
    downloadedAt: Date | string | null;
    declaredAt: Date | string | null;
    id: number;
    type: "urssaf" | "das2";
};

const quarterPeriodOptions = [1, 2, 3, 4];

/**
 * Returns start date and end date of period linked to open declaration (useful for fetching payments).
 * @param year number
 * @param quarter number
 */
export const getDeclarationPeriod = (
    year,
    quarter,
): { startDate: string; endDate: string; quarter?: number | undefined } => {
    let boundaries = {} as {
        startDate: string;
        endDate: string;
        quarter?: number;
    };
    if (quarterPeriodOptions.includes(quarter)) {
        boundaries = {
            startDate: `${year}-${+quarter * 3 - 2 < 10 ? "0" + (+quarter * 3 - 2) : +quarter * 3 - 2}-01T00:00:00`,
            endDate: `${year}-${+quarter * 3 < 10 ? "0" + +quarter * 3 : +quarter * 3}-${new Date(
                year,
                +quarter * 3,
                0,
            ).getDate()}T23:59:59`,
        };
        boundaries.quarter = quarter;
    } else {
        boundaries = {
            startDate: `${year}-01-01T00:00:00`,
            endDate: `${year}-12-31T23:59:59`,
        };
    }
    return boundaries;
};

export type URSSAFBeneficiaryError = {
    beneficiaryId: number;
    errors: { field: string; message: string }[];
    beneficiary: IBeneficiar & {
        computeFromRepresentative: number;
        beneficiaryId?: number;
        _representative: IRepresentative;
        beneficiary?: Partial<IBeneficiar>;
    };
};

/**
 * Formats URSSAF errors object to return array with one item per beneficiary.
 * @param errors
 */
export const formatURSSAFErrors = (errors: IUrssafErrors[]): URSSAFBeneficiaryError[] => {
    let formattedErrors: any = [];
    for (let i = 0; i < errors.length; i++) {
        const currentLine = errors[i].line;
        const lineErrors = {
            field: errors[i].field,
            message: errors[i].message,
        };
        const beneficiaryExists = formattedErrors.find((item) => item.beneficiaryId === currentLine.id);
        if (beneficiaryExists) {
            formattedErrors = formattedErrors.map((item) => {
                if (item.beneficiaryId === currentLine.id) {
                    return { ...item, errors: [...item.errors, lineErrors] };
                } else {
                    return item;
                }
            });
        } else {
            formattedErrors.push({
                beneficiaryId: currentLine.id,
                beneficiary: currentLine,
                errors: [lineErrors],
            });
        }
    }
    return formattedErrors;
};

/**
 * Period chosen formatted so as to add it in name of file they download:
 **/
export const getPeriodName = (year: number, quarter: number) => {
    return quarter === 0
        ? t("Payment.URSSAF.YearDownload", { year })
        : t("Payment.URSSAF.QuarterDownload", {
              year,
              trimester: quarter,
          });
};
/**
 * Checks social security number and returns boolean.
 * @returns boolean
 */
export const isNIRWrong = (NIR: string | undefined) => {
    if (!NIR) return false;
    const cleanNIR = NIR?.replace(/\s/g, "");
    if (!cleanNIR || cleanNIR.length < 15) return false;
    return !calculateNIRControlKey(NIR);
};

export const generateContributionLines = (
    data: IPayment[] | undefined,
    declaration: DeclarationsCardsData | undefined,
    companyInfos: ICompany | undefined,
) => {
    const lines = data?.map((item) => {
        const formattedStreet = divideAddress(item.beneficiary.street);

        return {
            declarationId: declaration?.id,
            companyId: companyInfos?.id,
            socialProfileType: item.beneficiary.socialProfileType,
            socialSecurityNumber: item.beneficiary.socialSecurityNumber,
            lastname: item.beneficiary.surname,
            firstname: item.beneficiary.name ?? "",
            street: formattedStreet.streetName,
            streetNumber: formattedStreet.streetNumber,
            streetNumberMultiplierAdverb: formattedStreet.multiplicativeAdverb,
            zipCode: item.beneficiary.zipCode,
            city: item.beneficiary.city,
            country: item.beneficiary.country,
            incomeNature: "002",
            artworkNature: companyInfos?.activityType === "publisher" ? "001" : "013",
            paymentDate: item.date.toString().split("T")[0],
            siretNumber: item.beneficiary.siretNumber,
            grossDuty: Math.round(+item.grossValue?.toString().replace(",", ".") * 100),
        };
    });

    return lines;
};

/**
 * Transforms the given address to an object containing the street number, street name and multiplicative adverb (B/T/Q/C)
 * @example
 * ```js
 * divideAddress("317 rue de Coulondres") => {
      streetNumber: "317",
      streetName: "rue de Coulondres",
      multiplicativeAdverb: ""
   }
 * divideAddress("317 bis rue de Coulondres") => {
      streetNumber: "317",
      streetName: "rue de Coulondres",
      multiplicativeAdverb: "B"
   }
 * ```
 * @param address The address to transform
 * @returns The street number, street name and the multiplicative adverb (B/T/Q/C)
 */
export const divideAddress = (address: string) => {
    const bis = (address.match(/(bis|b\.|b) /i) ?? [])[0];
    const ter = (address.match(/(ter|t\.|t) /i) ?? [])[0];
    const quater = (address.match(/(quater) /i) ?? [])[0];
    const quinquies = (address.match(/(quinquies) /i) ?? [])[0];
    const normalizedAddress = address
        .trim()
        .replace(/(bis|b\.|b|ter|t\.|t|quater|quinquies) /i, " ")
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "");
    const multiplicativeAdverb = bis ? "B" : quater ? "Q" : ter ? "T" : quinquies ? "C" : "";

    const streetNumber = normalizedAddress.split(" ")[0] ?? "";
    const streetName = normalizedAddress.split(" ")?.slice(1)?.join(" ")?.trim() ?? "";

    return { streetNumber, streetName, multiplicativeAdverb };
};

export const getPeriodYears = ({ periods }: { periods: TPeriod[] }) => {
    const years = new Set(periods.map((item) => new Date(item.periodEnd).getUTCFullYear()));
    return [...years];
};

/**
 * The same fetch on payments is called on Payments page and Declarations page.
 * To avoid a conflict between the need to fetch new data and a loop
 * (because I can't check if payments.length <= 0 as there might have been fetch before but I still need to do it again),
 * we save a the last fetch type in the localStorage so when we get to a page or the other we know if we need/can fetch again.
 * @param type Page from which the fetch is called.
 */
export const savePaymentFetchType = (type: "declarations" | "payments") => {
    localStorage.setItem("paymentFetchType", type);
};

export const getLateDeclarations = (contributions: IDeclaration[]) => {
    if (!contributions) return [];
    return contributions.filter((item) => new Date(item.endDate) < new Date() && !item.declaredAt);
};
